
import React from 'react';
import { Instagram, Twitter, Mail, Lock, Info, MapPin, Clock, Phone } from 'lucide-react';

const LogoEmblemSmall = ({ onClick }: { onClick?: () => void }) => (
  <button 
    id="logo-footer"
    onClick={onClick}
    className="relative w-10 h-10 group cursor-default active:scale-95 transition-transform"
    title="Celestial Hub"
  >
    <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/10 blur-xl rounded-full transition-all" />
    <svg viewBox="0 0 40 40" className="w-full h-full animate-[spin_30s_linear_infinite]">
      <path 
        d="M22 10C15.3726 10 10 15.3726 10 22C10 28.6274 15.3726 34 22 34C24.4682 34 26.7562 33.2536 28.6517 31.9774C23.1554 31.5414 18.8 26.9405 18.8 21.3333C18.8 15.7262 23.1554 11.1252 28.6517 10.6892C26.7562 9.41303 24.4682 8.66663 22 8.66663V10Z" 
        fill="#F59E0B"
      />
      <path d="M28 22L30 15L32 22L39 24L32 26L30 33L28 26L21 24L28 22Z" fill="white" />
    </svg>
  </button>
);

interface FooterProps {
  onAdminClick: () => void;
  onLegalClick: (type: 'privacy' | 'tos') => void;
}

const Footer: React.FC<FooterProps> = ({ onAdminClick, onLegalClick }) => {
  return (
    <footer className="bg-slate-950 border-t border-indigo-900/30 py-24 px-4 relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-8">
              <LogoEmblemSmall onClick={onAdminClick} />
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-[0.4em] text-amber-500 font-bold leading-none">Astrology</span>
                <span className="text-lg font-serif font-bold tracking-widest text-amber-50">BY ALLIE</span>
              </div>
            </div>
            <p className="text-indigo-200/60 leading-relaxed text-sm mb-8 italic">
              "Bridging the gap between the mundane and the celestial. Offering ethical, trauma-informed astrology for the modern seeker."
            </p>
            <div className="flex space-x-5">
              <a href="#" className="p-2.5 bg-indigo-950/50 border border-indigo-900/30 text-indigo-200 hover:text-amber-400 hover:border-amber-500 transition-all rounded-full"><Instagram size={18}/></a>
              <a href="#" className="p-2.5 bg-indigo-950/50 border border-indigo-900/30 text-indigo-200 hover:text-amber-400 hover:border-amber-500 transition-all rounded-full"><Twitter size={18}/></a>
              <a href="#" className="p-2.5 bg-indigo-950/50 border border-indigo-900/30 text-indigo-200 hover:text-amber-400 hover:border-amber-500 transition-all rounded-full"><Mail size={18}/></a>
            </div>
          </div>
          
          <div>
            <h4 className="text-amber-400 uppercase tracking-widest text-[10px] font-bold mb-8">Quick Navigation</h4>
            <ul className="space-y-4 text-xs uppercase tracking-widest text-indigo-200/80">
              <li><a href="#services" className="hover:text-amber-400 transition-colors">Services</a></li>
              <li><a href="#about" className="hover:text-amber-400 transition-colors">About Allie</a></li>
              <li><a href="#booking" className="hover:text-amber-400 transition-colors">Book a Session</a></li>
              <li><a href="#journal" className="hover:text-amber-400 transition-colors">Celestial Journal</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-amber-400 uppercase tracking-widest text-[10px] font-bold mb-8">Sacred Office</h4>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                 <MapPin size={18} className="text-amber-500 shrink-0" />
                 <div>
                    <p className="text-[11px] text-white uppercase tracking-wider font-bold mb-1">Location</p>
                    <p className="text-xs text-indigo-200/60 leading-relaxed">Los Angeles, California<br/>City of Angels, United States</p>
                 </div>
              </div>
              <div className="flex items-start gap-4">
                 <Clock size={18} className="text-amber-500 shrink-0" />
                 <div>
                    <p className="text-[11px] text-white uppercase tracking-wider font-bold mb-1">Availability</p>
                    <p className="text-xs text-indigo-200/60 leading-relaxed uppercase tracking-tighter">Open 24/7<br/>Aligning across all time zones</p>
                 </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-amber-400 uppercase tracking-widest text-[10px] font-bold mb-8">Sacred Notice</h4>
            <div className="p-6 bg-amber-500/5 border border-amber-500/10 rounded-sm">
               <div className="flex items-center gap-2 mb-4">
                  <Info size={14} className="text-amber-500" />
                  <span className="text-[9px] uppercase font-bold text-amber-500 tracking-widest">Disclaimer</span>
               </div>
               <p className="text-[10px] text-amber-200/40 italic leading-relaxed text-left">
                 Astrology is a tool for self-discovery and spiritual guidance. It is not a substitute for professional medical, legal, or financial advice. All readings are for educational and entertainment purposes.
               </p>
            </div>
          </div>
        </div>
        
        <div className="border-t border-indigo-900/20 pt-10 flex flex-col md:flex-row justify-between items-center text-[9px] uppercase tracking-[0.3em] text-indigo-500">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <p>© 2024 Astrology by Allie. All rights reserved.</p>
            <div className="flex space-x-6 items-center">
              <button onClick={() => onLegalClick('privacy')} className="hover:text-indigo-300 transition-colors uppercase tracking-[0.2em]">Privacy Policy</button>
              <button onClick={() => onLegalClick('tos')} className="hover:text-indigo-300 transition-colors uppercase tracking-[0.2em]">Terms of Service</button>
            </div>
          </div>
          <button onClick={onAdminClick} className="mt-6 md:mt-0 flex items-center gap-2 opacity-30 hover:opacity-100 hover:text-amber-500 transition-all group">
            <Lock size={12} className="group-hover:scale-110 transition-transform" /> Admin Gateway
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
